import * as ThemeUI from 'theme-ui';
import * as Icons from 'components/icons';
import { Popup } from 'components/Popup/Popup';
import React from 'react';
import { Flex } from 'components/layout';
import { Button, ButtonProps } from 'components/form-elements';
import { StyleObject } from 'theming';
import { FocusThief } from 'components/FocusThief/FocusThief';

type Props = {
  children: React.ReactNode | React.ReactNodeArray;
  icon?: React.ReactNode | React.ReactNodeArray;
  sx?: StyleObject;
};

export const Submenu = ({ children, icon, sx }: Props) => {
  /*
   * "reactjs-popup" renders this menu with `position: fixed` and only
   * recomputes its coordinates on window `resize` (never on scroll). That
   * makes the open menu stay pinned to the viewport while the trigger scrolls
   * away. Dispatching a synthetic `resize` event on scroll forces the popup to
   * reposition against its trigger's current bounding rect, so the menu stays
   * anchored right beneath the three-dots button while scrolling.
   * The scroll listener is only active while the menu is open. `capture: true`
   * ensures scrolling inside any nested scroll container is caught too.
   */
  const repositionOnScroll = React.useCallback(() => {
    window.dispatchEvent(new Event('resize'));
  }, []);

  const handleOpen = React.useCallback(() => {
    window.addEventListener('scroll', repositionOnScroll, true);
  }, [repositionOnScroll]);

  const handleClose = React.useCallback(() => {
    window.removeEventListener('scroll', repositionOnScroll, true);
  }, [repositionOnScroll]);

  React.useEffect(
    () => () => window.removeEventListener('scroll', repositionOnScroll, true),
    [repositionOnScroll],
  );

  return (
    <Popup
      closeOnContentClick
      position={['bottom right', 'top right']}
      keepTooltipInside
      onOpen={handleOpen}
      onClose={handleClose}
      trigger={(
        <ThemeUI.Box
          sx={{
            display: 'inline-flex',
          }}
        >
          {icon}
          {!icon && <Icons.ThreeDots width={48} />}
        </ThemeUI.Box>
      )}
      arrow={false}
    >
      <FocusThief />

      <Flex
        flexDirection="column"
        alignItems="flex-start"
        sx={{
          borderRadius: '7px',
          backgroundColor: 'grey.100',
          color: 'grey.400',
          fontWeight: '200',
          fontSize: 'sm',
          position: 'relative',
          boxShadow: '4dp',

          '& > *:not(:last-child)': {
            borderBottom: '1px solid',
            borderColor: 'grey.300',
            borderBottomLeftRadius: '0px',
            borderBottomRightRadius: '0px',
          },

          ...sx,
        }}
      >
        {children}
      </Flex>
    </Popup>
  );
};

type SubmenuItemProps = ButtonProps;

Submenu.Item = function SubmenuItem({
  sx,
  children,
  ...props
}: SubmenuItemProps) {
  return (
    <Button
      {...props}
      sx={{
        p: 3,
        width: '100%',
        color: 'secondary.400',
        fontWeight: 400,
        fontSize: 'md',
        justifyContent: 'flex-start',
        ...sx,
      }}
      variant="text"
    >
      {children}
    </Button>
  );
};
